import { useEffect, useState } from "react";
import { Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAdminConfig, useSetAdminConfig } from "@/hooks/useAdmin";
import { formatRelative } from "@/lib/format";
import type { AppConfigEntry } from "@/lib/api-types";

const DEFAULT_KEY = "ai_chat_daily_limit_per_user";

type ValueType = AppConfigEntry["value_type"];

/** Coerce the raw input string to the chosen value_type for the POST body. */
function coerce(raw: string, type: ValueType): string | number | boolean {
  if (type === "number") return Number(raw);
  if (type === "boolean") return raw === "true";
  return raw;
}

export function AdminConfigPage() {
  const [key, setKey] = useState(DEFAULT_KEY);
  // `loadedKey` is the key we last fetched. Editing the key field doesn't
  // refetch until the admin presses Load -- avoids a query per keystroke.
  const [loadedKey, setLoadedKey] = useState(DEFAULT_KEY);

  const query = useAdminConfig(loadedKey);
  const mutation = useSetAdminConfig();

  const [valueType, setValueType] = useState<ValueType>("number");
  const [value, setValue] = useState("");
  const [description, setDescription] = useState("");

  // Sync the editable form with the freshly-loaded config row. When the key
  // has never been written (data === null) we keep sensible defaults.
  useEffect(() => {
    if (query.data) {
      setValueType(query.data.value_type);
      setValue(String(query.data.value));
      setDescription(query.data.description ?? "");
    } else if (query.data === null && !query.isLoading) {
      setValue("");
      setDescription("");
    }
  }, [query.data, query.isLoading]);

  const onLoad = () => {
    const k = key.trim();
    if (k) setLoadedKey(k);
  };

  const onSave = () => {
    if (value.trim() === "") {
      toast.error("Value is required");
      return;
    }
    if (valueType === "number" && Number.isNaN(Number(value))) {
      toast.error("Value must be a number");
      return;
    }
    mutation.mutate(
      {
        key: loadedKey,
        value: coerce(value, valueType),
        value_type: valueType,
        description: description || undefined,
      },
      {
        onSuccess: () => toast.success(`Saved '${loadedKey}'`),
        onError: () => toast.error("Failed to save config"),
      },
    );
  };

  return (
    <div className="max-w-2xl">
      <Card className="p-5">
        <h3 className="text-sm font-semibold text-text">Application config</h3>
        <p className="mt-1 text-sm text-text-muted">
          Read and write a single runtime config key. Changes are audited.
        </p>

        {/* Key + load */}
        <div className="mt-5 flex items-end gap-2">
          <label className="flex flex-1 flex-col gap-1 text-xs font-medium text-text-muted">
            Config key
            <Input
              value={key}
              onChange={(e) => setKey(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onLoad();
              }}
              placeholder="e.g. ai_chat_daily_limit_per_user"
            />
          </label>
          <Button variant="secondary" onClick={onLoad} loading={query.isFetching}>
            <RotateCcw className="h-4 w-4" />
            Load
          </Button>
        </div>

        {query.isLoading ? (
          <Skeleton className="mt-5 h-40 w-full" />
        ) : (
          <div className="mt-5 flex flex-col gap-4 border-t border-border pt-5">
            {query.data === null ? (
              <p className="rounded-lg bg-surface-2 px-3 py-2 text-sm text-text-muted">
                No value set for <span className="font-mono">{loadedKey}</span> yet -- fill in
                the form below to create it.
              </p>
            ) : null}

            <label className="flex flex-col gap-1 text-xs font-medium text-text-muted">
              Value type
              <select
                value={valueType}
                onChange={(e) => setValueType(e.target.value as ValueType)}
                className="h-10 rounded-lg border border-border bg-surface px-3 text-sm text-text dark:bg-surface-2 focus-visible:border-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
              >
                <option value="number">number</option>
                <option value="string">string</option>
                <option value="boolean">boolean</option>
              </select>
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-text-muted">
              Value
              {valueType === "boolean" ? (
                <select
                  value={value || "false"}
                  onChange={(e) => setValue(e.target.value)}
                  className="h-10 rounded-lg border border-border bg-surface px-3 text-sm text-text dark:bg-surface-2 focus-visible:border-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
                >
                  <option value="true">true</option>
                  <option value="false">false</option>
                </select>
              ) : (
                <Input
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  inputMode={valueType === "number" ? "numeric" : "text"}
                  placeholder={valueType === "number" ? "e.g. 25" : "Value"}
                />
              )}
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-text-muted">
              Description (optional)
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What this key controls"
              />
            </label>

            {query.data?.updated_at ? (
              <p className="text-xs text-text-subtle">
                Last updated {formatRelative(query.data.updated_at)}
                {query.data.updated_by ? ` by ${query.data.updated_by}` : ""}
              </p>
            ) : null}

            <div>
              <Button onClick={onSave} loading={mutation.isPending}>
                <Save className="h-4 w-4" />
                Save
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
